import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@clerk/clerk-react";
import { accessApi } from "@/lib/api/access";
import { type User, type UserRole } from "@/types/access";

export function useUsers() {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const [users, setUsers] = useState<(User & { role: UserRole })[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(12);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const fetchUsers = useCallback(async () => {
    if (!isLoaded || !isSignedIn) return;
    
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      if (!token) {
        throw new Error("No authentication token available");
      }
      
      const response = await accessApi.getUsers(token, page, limit);
      setUsers(response.users);
      setTotal(response.total);
    } catch (err) {
      console.error("Failed to fetch users:", err);
      setError(err instanceof Error ? err.message : "An unknown error occurred");
    } finally {
      setLoading(false);
    }
  }, [isLoaded, isSignedIn, getToken, page, limit]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers, refreshTrigger]);

  const updateUserRole = async (userId: string, newRole: UserRole) => {
    try {
      const token = await getToken();
      if (!token) throw new Error("No authentication token available");

      await accessApi.setUserRole(token, userId, newRole);
      
      // Optimistic update or refresh
      setUsers(currentUsers => 
        currentUsers.map(user => 
          user.id === userId ? { ...user, role: newRole } : user
        )
      );
      
      return true;
    } catch (err) {
      console.error("Failed to update user role:", err);
      // Revert optimistic update if we implemented it, or simpler: just show error
      // Since we did optimistic update above, we might want to refresh to be safe or handle rollback
      return false;
    }
  };

  const deleteUserRole = async (userId: string) => {
    try {
       const token = await getToken();
      if (!token) throw new Error("No authentication token available");

      await accessApi.deleteUserRole(token, userId);
      
      // Optimistic update to default 'user' role
      setUsers(currentUsers => 
        currentUsers.map(user => 
          user.id === userId ? { ...user, role: "user" } : user
        )
      );
      return true;
    } catch (err) {
      console.error("Failed to reset user role:", err);
      return false;
    }
  };

  const refresh = () => setRefreshTrigger(prev => prev + 1);

  return {
    users,
    total,
    page,
    setPage,
    limit,
    setLimit,
    loading,
    error,
    updateUserRole,
    deleteUserRole,
    refresh
  };
}
